<template>
  <ModalWrapper :is-cancellable="false">
    <div class="p-32 pt-48">
      <div v-if="uiStore.sellerDialogSettings.clubInactive" class="text-lg mb-16 text-warning">
        {{ $lang('seller', 'sales_closed') }}
      </div>
      <div v-if="uiStore.sellerDialogSettings.clubPastDeadline" class="text-lg mb-16 text-warning">
        {{ $lang('seller', 'sales_past_deadline') }}
      </div>
      <div v-if="uiStore.sellerDialogSettings.sellerError || uiStore.sellerDialogSettings.sellerMissing">
        <div class="text-lg mb-16">
          <span v-if="uiStore.sellerDialogSettings.forceSellerSelection">
            {{ $lang('seller', 'choose_seller') }}
          </span>
          <span v-else-if="uiStore.sellerDialogSettings.sellerError">
            {{ $lang('seller', 'seller_not_found') }}
          </span>
          <span v-else-if="!uiStore.sellerDialogSettings.clubPastDeadline && !uiStore.sellerDialogSettings.clubInactive">
            {{ $lang('seller', 'enter_seller_number') }}
          </span>
        </div>
        <div>
          <TextInput
            :placeholder="$lang('seller', 'enter_seller_number')"
            has-search-icon
            type="number"
            :has-error="error"
            :is-loading="loading"
            @submit-input="(e) => sellerNo = e"
          />
        </div>
      </div>

      <div v-if="foundInactiveClub" class="mt-16">
        {{ $lang('seller', 'club_inactive') }}
      </div>
      <div v-if="foundSeller" class="mt-16">
        {{ $lang('seller', 'selected_seller', {
          'seller': sellerCookie?.Name || '',
          'club': clubCookie?.Name || '',
        })
        }}
      </div>
    </div>
    <div v-if="uiStore.sellerDialogSettings.forceSellerSelection" class="px-32 pb-16 text-right">
      <button
        class="text-xs text-gray-500 hover:text-gray-700 underline"
        @click="optOutOfSupport"
      >
        {{ $lang('seller', 'opt_out_support') }}
      </button>
    </div>
    <div class="flex justify-between">
      <button
        v-if="isCancellable"
        class="basis-1/2 btn btn--secondary btn--lg rounded-none"
        :class="{
          'basis-1/2': isCancellable,
          'basis-full': !showOkButton
        }"
        @click="clickCancel"
      >
        {{ $lang('seller', 'cancel') }}
      </button>
      <button
        v-if="showOkButton"
        class="btn btn--primary btn--lg rounded-none"
        :class="{
          'basis-1/2': isCancellable,
          'basis-full': !isCancellable
        }"
        :disabled="!okClickable"
        @click="clickOk"
      >
        {{ $lang('seller', 'ok') }}
      </button>
    </div>
  </ModalWrapper>
</template>

<script setup lang="ts">
import type { Ref } from 'vue';
import ModalWrapper from '~/components/global/ModalWrapper.vue';
import TextInput from '~/components/forms/TextInput.vue';
import type { SellerClub, SellerPerson } from '~/constants/types/seller';
import useAlgoliaSearch from '~/composables/useAlgoliaSearch';
import { pushToDataLayer } from '~/composables/useTracking';
import { useUiStore } from '~/store/ui';
import { useConfigStore } from "~/store/config";
const { algoliaClubSearch, algoliaSellerSearch } = useAlgoliaSearch();

const uiStore = useUiStore();
const config = useConfigStore();

const isCancellable = computed(()=> {
  if (uiStore.sellerDialogSettings.forceSellerSelection) {
    return foundInactiveClub.value;
  }
  return (uiStore.sellerDialogSettings.sellerMissing || uiStore.sellerDialogSettings.sellerError);
});

const { sellerCookieOptions } = useCookieConfig();
const clubCookie = useCookie<SellerClub | null>('club', sellerCookieOptions) as Ref<SellerClub | null>;
const sellerCookie = useCookie<SellerPerson | null>('seller', sellerCookieOptions) as Ref<SellerPerson | null>;
const isSeller = useCookie<boolean>('isSeller', sellerCookieOptions) as Ref<boolean>;
const invoiceRejected = useCookie<boolean>('invoiceRejected', { ...sellerCookieOptions, default: () => false }) as Ref<boolean>;

const foundInactiveClub = ref(false);

const okClickable =computed(()=> {
  if (foundSeller.value) {
    return true;
  }
  if (uiStore.sellerDialogSettings.clubInactive) {
    return true;
  }
  if (uiStore.sellerDialogSettings.clubPastDeadline) {
    return true;
  }
  return false;
});

const showOkButton = computed(()=> {
  if (foundInactiveClub.value) {
    return false;
  }
  return true;
  //return (uiStore.sellerDialogSettings.clubInactive);
});

const clickOk = () => {
  if (pendingRedirect.value) {
    // Redirect to correct market when fallback was used
    // Use window.location for full page reload (client-side router doesn't know about the route yet)
    console.log(`[SellerDialog] User confirmed, redirecting to ${pendingRedirect.value}`);
    window.location.href = pendingRedirect.value;
  } else if (uiStore.sellerDialogSettings.clubInactive) {
    clubCookie.value = null;
    sellerCookie.value = null;
    isSeller.value = false;
    setTimeout(()=> {
      window.location.href = '/se';
    },250);
  } else {
    close();
  }
};

const clickCancel = () => {
  clubCookie.value = null;
  sellerCookie.value = null;

  if (foundInactiveClub.value) {
    isSeller.value = false;
  }
  close();
};

const optOutOfSupport = () => {
  clubCookie.value = null;
  sellerCookie.value = null;
  isSeller.value = false;
  config.isSeller = false;
  close();
  // Reload the page to refresh checkout state
  setTimeout(() => {
    window.location.reload();
  }, 250);
};

const sellerNo = ref('');
const foundSeller = ref(false);
const error = ref(false);
const pendingRedirect = ref<string | null>(null);
watch(sellerNo, ()=> {
  foundSeller.value = false;
  error.value = false;
  foundInactiveClub.value = false;
  pendingRedirect.value = null;
  doSearch();
});
const today = Date.now();

const loading = ref(false);
const doSearch = async() => {
  if (sellerNo.value.length >= 8) {
    loading.value = true;
    const sellerRes = await algoliaSellerSearch(sellerNo.value);
    if (sellerRes.hits && sellerRes.hits.length === 1) {
      // Check if fallback index was used
      const currentBaseIndex = config.algoliaBaseIndex.replace('fi-sv', 'fi');
      const usedFallback = sellerRes.usedIndex !== currentBaseIndex;
      
      if (usedFallback) {
        console.log(`[SellerDialog] Found in fallback index (${sellerRes.usedIndex}), will redirect after confirmation`);
        
        // Determine target language for redirect
        let targetLang: string;
        if (sellerRes.usedIndex.includes('_fi')) {
          // Swedish visitor found Finnish seller → redirect to Finnish-Swedish
          targetLang = 'fi-se';
        } else {
          // Finnish visitor found Swedish seller → redirect to Swedish
          targetLang = 'se';
        }
        
        // Construct redirect URL
        const sellerId = sellerRes.hits[0].WebshopToken;
        const fullPath = `/${targetLang}/seller/${sellerId}`;
        
        // Save redirect URL for when user clicks OK
        pendingRedirect.value = fullPath;
        console.log(`[SellerDialog] Pending redirect to ${fullPath}`);
      }
      
      pushToDataLayer({
        event: 'sellerId',
        sellerId: sellerRes.hits[0].WebshopToken,
      });
      // Search club in same index that found the seller
      const clubRes = await algoliaClubSearch(sellerRes.hits[0].RoundID, sellerRes.usedIndex);

      if (clubRes) {
        if (clubRes.length !== 1 || !clubRes[0].Active) {
          if (clubRes.length !== 1) {
            console.log('Wrong number clubs: ' + clubRes.length);
            error.value = true;
          } else {
            console.log('Inactive club');
            foundInactiveClub.value = true;
            clubCookie.value = null;
            sellerCookie.value = null;
          }
        } else {
          // We have both our seller and our club
          const openShop = ref(false);
          if (clubRes[0].NoDeadlineDate) {
            openShop.value = true;
          } else if (clubRes[0].DeadlineDate?.length > 0) {
            let closingDate = Date.parse(clubRes[0].DeadlineDate);
            closingDate += 86400000; // add 24 hours to open also closing date
            if (closingDate >= today) {
              openShop.value = true;
            }
          }
          config.clubCookie = clubRes[0];
          config.sellerCookie = sellerRes.hits[0];
          clubCookie.value = clubRes[0];
          sellerCookie.value = sellerRes.hits[0];
          foundSeller.value = true;
          if (!openShop.value) {
            // Club is past deadline date
            uiStore.sellerDialogSettings.clubPastDeadline = true;
            invoiceRejected.value = true;
          } else {
            uiStore.sellerDialogSettings.clubPastDeadline = false;
            invoiceRejected.value = false;
          }
          isSeller.value = true;
        }
      } else {
        console.log('Club error');
        error.value = true;
      }
    } else {
      error.value = true;
    }
  }
  loading.value = false;
};

const close = () => {
  uiStore.sellerDialogSettings = {};
  uiStore.showSellerDialog = false;
};

</script>

<style scoped lang="postcss">
button:disabled {
  opacity: 0.4;
}
</style>
